import { TFunction } from 'react-i18next';
import * as Yup from 'yup';

import { FormDataHub, HubType } from '../../../../../../types/api/hubs';
import { AddressInitialValues } from '../../../../../../utils/constants/address';
import { isStreetFreeInputAllowed } from '../../../../../../utils/constants/misc';
import { invalidTabHeaderIcon } from '../../../../../../utils/globals';
import {
  formatMobileNumber,
  formatPhoneNumber,
  getPhoneorMobileNumberRegion,
  isValidMobileNumber,
  isValidPhoneNumber,
} from '../../../../../../utils/helpers/phoneNumbers';
import { isEmail } from '../../../../../../utils/helpers/validation';
import {
  yupMaxField,
  yupMinField,
  yupRequiredField,
} from '../../../../../../utils/helpers/yup';
import { getAddressSchema } from '../../../../../validations/address';

type TabName = 'general-info' | 'intervals' | 'working-hours' | 'regions';

const fields = {
  reconciliationTimeInterval: [
    {
      dayOfWeek: 1,
      timeSpanFrom: '07:00',
      timeSpanTo: '15:00',
    },
    {
      dayOfWeek: 2,
      timeSpanFrom: '07:00',
      timeSpanTo: '15:00',
    },
    {
      dayOfWeek: 3,
      timeSpanFrom: '07:00',
      timeSpanTo: '15:00',
    },
    {
      dayOfWeek: 4,
      timeSpanFrom: '07:00',
      timeSpanTo: '15:00',
    },
    {
      dayOfWeek: 5,
      timeSpanFrom: '07:00',
      timeSpanTo: '15:00',
    },
  ],
  workingHours: [
    {
      dayOfWeek: 1,
      openingHours: [{ from: '07:00', to: '15:00' }],
    },
    {
      dayOfWeek: 2,
      openingHours: [{ from: '07:00', to: '15:00' }],
    },
    {
      dayOfWeek: 3,
      openingHours: [{ from: '07:00', to: '15:00' }],
    },
    {
      dayOfWeek: 4,
      openingHours: [{ from: '07:00', to: '15:00' }],
    },
    {
      dayOfWeek: 5,
      openingHours: [{ from: '07:00', to: '15:00' }],
    },
  ],
};

export function getDays(t: TFunction) {
  return [
    { label: t('Monday'), dayOfWeek: 1 },
    { label: t('Tuesday'), dayOfWeek: 2 },
    { label: t('Wednesday'), dayOfWeek: 3 },
    { label: t('Thursday'), dayOfWeek: 4 },
    { label: t('Friday'), dayOfWeek: 5 },
    { label: t('Saturday'), dayOfWeek: 6 },
    { label: t('Sunday'), dayOfWeek: 7 },
  ];
}

export function getInitialValues(
  data: HubType | undefined,
  isEditDialog: boolean
) {
  return {
    name: isEditDialog ? data?.name ?? '' : '',
    mobile: isEditDialog ? data?.mobile ?? '' : '',
    mobile_region: getPhoneorMobileNumberRegion(''),
    phone: isEditDialog ? data?.phone ?? '' : '',
    phone_region: getPhoneorMobileNumberRegion(''),
    email: isEditDialog ? data?.email ?? '' : '',
    code: isEditDialog ? data?.code ?? '' : '',
    address: isEditDialog
      ? data?.address ?? AddressInitialValues
      : AddressInitialValues,
    franchiseId: isEditDialog ? data?.franchiseId ?? 0 : 0,
    // partnerName: isEditDialog ? data?.partnerName ?? '' : '',
    sortingCenter: isEditDialog ? data?.sortingCenter ?? false : false,
    dropPoint: isEditDialog ? data?.dropPoint ?? false : false,
    branchManagerId: isEditDialog ? data?.branchManagerId ?? 0 : 0,
    // branchManagerName: isEditDialog ? data?.branchManagerName ?? '' : '',
    regions: isEditDialog ? data?.regions ?? [] : [],
    audio: isEditDialog ? data?.audio ?? '' : '',
    reconciliationTimeInterval: isEditDialog
      ? data?.reconciliationTimeInterval ?? fields.reconciliationTimeInterval
      : fields.reconciliationTimeInterval,
    workingHours: isEditDialog
      ? data?.workingHours ?? fields.workingHours
      : fields.workingHours,
  };
}

export function toApiData(values: FormDataHub) {
  const formData = new FormData();
  let apiData = {
    ...values,
  };

  if (!apiData.email) {
    delete apiData.email;
  }

  if (!apiData.code) {
    delete apiData.code;
  }

  if (!apiData.franchiseId) {
    delete apiData.franchiseId;
  }

  if (!apiData.branchManagerId) {
    delete apiData.branchManagerId;
  }

  if (!apiData.sortingCenter) {
    delete apiData.sortingCenter;
  }

  if (!apiData.dropPoint) {
    delete apiData.dropPoint;
  }

  if (apiData.phone && apiData.phone_region) {
    apiData.phone = formatPhoneNumber(apiData.phone, apiData.phone_region);
  } else {
    delete apiData.phone;
  }

  if (apiData.mobile && apiData.mobile_region) {
    apiData.mobile = formatMobileNumber(apiData.mobile, apiData.mobile_region);
  } else {
    delete apiData.mobile;
  }

  if (apiData.audio) {
    formData.append('audio', apiData.audio);
  } else {
    delete apiData.audio;
  }

  if (Array.isArray(apiData.regions)) {
    apiData.regions = apiData.regions.map((r: any) =>
      typeof r === 'object' ? r.id : r
    );
  }

  delete apiData.phone_region;
  delete apiData.mobile_region;

  formData.append('data', JSON.stringify(apiData));

  return formData;
}

export function getValidationSchema(t: TFunction) {
  return Yup.object().shape({
    name: Yup.string()
      .required(yupRequiredField(t, t('Name')))
      .min(1, ({ min }) => yupMinField(t, t('Name'), min))
      .max(45, ({ max }) => yupMaxField(t, t('Name'), max)),
    email: Yup.string().test(
      'email',
      t('Invalid email address entered!'),
      (value: any) => {
        if (value?.length > 0) {
          return isEmail(value);
        } else {
          return true;
        }
      }
    ),
    code: Yup.string().optional(),
    mobile: Yup.string()
      .test(
        'valid-mobile',
        t('Invalid mobile phone number'),
        (value, context) => {
          if (!value) return true;
          const { mobile_region } = context.parent;
          return isValidMobileNumber(value, mobile_region);
        }
      )
      .test(
        'mobile-or-phone',
        t('At least mobile or phone number must be filled out'),
        function (value) {
          const { phone } = this.parent;
          return !!(value || phone);
        }
      ),

    phone: Yup.string().test(
      'valid-phone',
      t('Invalid phone number'),
      (value, context) => {
        if (!value) return true;
        const { phone_region } = context.parent;
        return isValidPhoneNumber(value, phone_region);
      }
    ),

    audio: Yup.mixed().nullable().notRequired(),
    reconciliationTimeInterval: Yup.array()
      .of(
        Yup.object().shape({
          dayOfWeek: Yup.number().required(),
          timeSpanFrom: Yup.string()
            .required(t('Required'))
            .matches(/^\d{2}:\d{2}$/, t('Must be in HH:MM')),
          timeSpanTo: Yup.string()
            .required(t('Required'))
            .matches(/^\d{2}:\d{2}$/, t('Must be in HH:MM')),
        })
      )
      .test(
        'from-before-to',
        t('Start time must be before end time for each day'),
        function (intervals) {
          if (!Array.isArray(intervals)) return true;
          for (let i = 0; i < intervals.length; i++) {
            const iv: any = intervals[i];
            if (iv.dayOfWeek === 5 || iv.dayOfWeek === 6) continue;

            const [h1, m1] = iv.timeSpanFrom.split(':').map(Number);
            const [h2, m2] = iv.timeSpanTo.split(':').map(Number);
            if (h1 > h2 || (h1 === h2 && m1 > m2)) {
              return this.createError({
                path: `reconciliationTimeInterval[${intervals.indexOf(
                  iv
                )}].timeSpanFrom`,
                message: t('Start time must be before end time for each day'),
              });
            }
          }
          return true;
        }
      ),
    workingHours: Yup.array().of(
      Yup.object().shape({
        dayOfWeek: Yup.number().required(),
        openingHours: Yup.array()
          .of(
            Yup.object().shape({
              from: Yup.string()
                .required(t('Required'))
                .matches(/^\d{2}:\d{2}$/, t('Must be in HH:MM')),
              to: Yup.string()
                .required(t('Required'))
                .matches(/^\d{2}:\d{2}$/, t('Must be in HH:MM')),
            })
          )

          .test(
            'from-before-to',
            t('Start time must be before end time for each day'),
            function (openingHours) {
              if (!Array.isArray(openingHours)) return true;

              const sorted = [...openingHours].sort((a: any, b) =>
                a.from.localeCompare(b.from)
              );

              for (let i = 0; i < sorted.length; i++) {
                const { from, to }: any = sorted[i];
                const [h1, m1] = from.split(':').map(Number);
                const [h2, m2] = to.split(':').map(Number);

                if (h1 > h2 || (h1 === h2 && m1 > m2)) {
                  return this.createError({
                    message: t(
                      'Start time must be before end time for each day'
                    ),
                  });
                }

                if (i < sorted.length - 1) {
                  const next: any = sorted[i + 1];
                  const [nh1, nm1] = next.from.split(':').map(Number);
                  if (h2 > nh1 || (h2 === nh1 && m2 > nm1)) {
                    return this.createError({
                      message: t(
                        'Start time must be before end time for each day'
                      ),
                    });
                  }
                }
              }

              return true;
            }
          ),
      })
    ),
    franchiseId: Yup.number().nullable(),
    branchManagerId: Yup.number().nullable(),
    regions: Yup.array().nullable(),
    sortingCenter: Yup.boolean().nullable().optional(),
    dropPoint: Yup.boolean().nullable().optional(),
    address: getAddressSchema(t, {
      isStreetFreeInputAllowed,
      municipality: true,
      RequiredHouseNumber: true,
    }),
  });
}

export function tabIcon(tabName: TabName, values: FormDataHub, errors: any) {
  const hasTabErrors = getHasTabErrors();
  return hasTabErrors(tabName, values, errors) ? invalidTabHeaderIcon : {};
}

function getHasTabErrors() {
  return function hasTabErrors(
    tabName: TabName,
    values: FormDataHub,
    errors: any
  ) {
    const errorsKeys = Object.keys(errors);

    const tabNameFieldsMap = {
      'general-info': {
        name: true,
        address: true,
      },
      intervals: !values.reconciliationTimeInterval,
      'working-hours': !values.workingHours,
      regions: !values.regions,
    };

    const tabFields = Object.keys(tabNameFieldsMap[tabName]);

    for (const field of tabFields) {
      if (errorsKeys.includes(field)) {
        return true;
      }
    }

    return false;
  };
}
